import { createFileRoute, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getReceiptForDownload } from "@/lib/documents.functions";
import { DocFooter, DocHeader, PrintActions, formatMoney, printStyles } from "@/components/documents/doc-shell";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/receipt/$orderId")({
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/login" });
  },
  head: () => ({ meta: [{ title: "Order receipt" }] }),
  component: ReceiptDownloadPage,
});

function ReceiptDownloadPage() {
  const { orderId } = Route.useParams();
  const fetchReceipt = useServerFn(getReceiptForDownload);
  const { data, isLoading, error } = useQuery({
    queryKey: ["receipt-download", orderId],
    queryFn: () => fetchReceipt({ data: { order_id: orderId } }),
  });

  if (isLoading) return <div className="p-10 text-center text-sm text-muted-foreground">Loading receipt…</div>;
  if (error || !data) {
    return <div className="p-10 text-center text-sm text-destructive">Unable to load receipt. {(error as Error)?.message}</div>;
  }

  const { order, items, branding } = data;
  const shortRef = order.id.slice(0, 8).toUpperCase();

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6 print:p-0">
      <style>{printStyles}</style>
      <PrintActions title={`Receipt #${shortRef}`} />

      <div className="rounded-lg border bg-card p-8 shadow-sm print:border-0 print:shadow-none">
        <DocHeader
          branding={branding}
          title="RECEIPT"
          subtitle={`Order #${shortRef}`}
        />

        <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground">Customer</div>
            <div className="mt-1 font-medium">{order.customer_name ?? "—"}</div>
            {order.customer_email && <div className="text-muted-foreground">{order.customer_email}</div>}
          </div>
          <div className="text-right space-y-1">
            <div className="flex justify-end gap-3"><span className="text-muted-foreground">Status</span>
              <Badge variant={order.status === "paid" || order.status === "fulfilled" ? "default" : "secondary"} className="capitalize">{order.status}</Badge>
            </div>
            <div className="flex justify-end gap-3"><span className="text-muted-foreground">Order date</span>
              <span>{new Date(order.created_at).toLocaleDateString()}</span>
            </div>
            {order.paid_at && (
              <div className="flex justify-end gap-3"><span className="text-muted-foreground">Paid</span>
                <span>{new Date(order.paid_at).toLocaleDateString()}</span>
              </div>
            )}
            {order.tracking_number && (
              <div className="flex justify-end gap-3"><span className="text-muted-foreground">Tracking</span>
                <span>{order.tracking_carrier ? `${order.tracking_carrier} · ` : ""}{order.tracking_number}</span>
              </div>
            )}
          </div>
        </div>

        <table className="mt-8 w-full text-sm">
          <thead style={{ background: branding.accent_color + "15" }}>
            <tr>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit price</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b">
                <td className="px-3 py-2">
                  <div className="font-medium">{i.product_name}</div>
                  {i.variant_name && <div className="text-xs text-muted-foreground">{i.variant_name}</div>}
                </td>
                <td className="px-3 py-2 text-right">{i.qty}</td>
                <td className="px-3 py-2 text-right">{formatMoney(i.unit_price_cents, order.currency)}</td>
                <td className="px-3 py-2 text-right">{formatMoney(i.line_total_cents, order.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatMoney(order.subtotal_cents, order.currency)}</span></div>
            {order.discount_cents > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>− {formatMoney(order.discount_cents, order.currency)}</span></div>
            )}
            <div className="flex justify-between border-t pt-2 text-base font-semibold" style={{ borderColor: branding.accent_color }}>
              <span>Total paid</span><span>{formatMoney(order.total_cents, order.currency)}</span>
            </div>
          </div>
        </div>

        {order.notes && (
          <div className="mt-6 rounded-md border bg-muted/30 p-3 text-sm">
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Notes</div>
            {order.notes}
          </div>
        )}

        <DocFooter branding={branding} />
      </div>
    </div>
  );
}
